import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import Button from "./UiElements/Button";
import Menu3Dots from "./icons/Menu3Dots";
import { tokens } from "../design-system/tokens";
import styles from "./TestDriveCarRowActions.module.css";
import useIsMobile from "../hooks/useIsMobile";

type StatusType = "available" | "pending" | "occupied";

export type TestDriveCarRowActionsCar = {
  _id: string;
  status: StatusType;
};

export type TestDriveCarRowActionsProps = {
  car: TestDriveCarRowActionsCar;
  onStartTestDrive: () => void;
  onForceStartWithoutApproval: () => void;
  onCancelPending: () => void;
  onEnd: () => void;
  onFeedback: () => void;
  onHistory: () => void;
  onExport: () => void;
  onDelete: () => void;
};

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 8;

const menuPanelStyle: React.CSSProperties = {
  backgroundColor: tokens.colors.background,
  boxShadow: tokens.shadows.md,
  borderRadius: tokens.borderRadius.xl,
  ["--td-actions-menu-item-hover-bg" as string]: tokens.buttonColors.secondaryLightHover,
};

const menuItemStyle: React.CSSProperties = {
  ...tokens.typography.menuItem,
  color: tokens.colors.text.primary,
  borderRadius: tokens.borderRadius.md,
};

const TestDriveCarRowActions: React.FC<TestDriveCarRowActionsProps> = ({
  car,
  onStartTestDrive,
  onForceStartWithoutApproval,
  onCancelPending,
  onEnd,
  onFeedback,
  onHistory,
  onExport,
  onDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const portalMenuRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const handleToggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  useLayoutEffect(() => {
    if (!menuOpen || !triggerRef.current || !portalMenuRef.current) {
      setMenuPosition(null);
      return;
    }

    const anchor = triggerRef.current.getBoundingClientRect();
    const menu = portalMenuRef.current;
    const menuRect = menu.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = anchor.bottom + ANCHOR_GAP;
    if (top + menuRect.height > vh - VIEWPORT_MARGIN) {
      top = anchor.top - menuRect.height - ANCHOR_GAP;
    }
    top = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - menuRect.height - VIEWPORT_MARGIN));

    let left = anchor.right - menuRect.width;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - menuRect.width - VIEWPORT_MARGIN));

    setMenuPosition({ top, left });
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const isInsideTargets = (node: Node | null) => {
      if (!node) return false;
      if (triggerRef.current?.contains(node)) return true;
      if (portalMenuRef.current?.contains(node)) return true;
      return false;
    };

    const handlePointer = (event: MouseEvent | TouchEvent) => {
      const target = (event.target as Node) ?? null;
      if (!isInsideTargets(target)) {
        setMenuOpen(false);
      }
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    const handleScrollOrResize = () => {
      setMenuOpen(false);
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer, { passive: true });
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [menuOpen]);

  const floatingStyle: React.CSSProperties =
    menuOpen && menuPosition
      ? { top: menuPosition.top, left: menuPosition.left, visibility: "visible" }
      : { top: -9999, left: -9999, visibility: "hidden" };

  return (
    <motion.div className={styles.actions} layout>
      {car.status === "available" ? (
        <Button
          label={isMobile ? "התחל" : "התחל נסיעת מבחן"}
          color="secondary"
          size="small"
          width={isMobile ? undefined : 150}
          onClick={onStartTestDrive}
        />
      ) : car.status === "pending" ? (
        <Button
          label={isMobile ? "ללא אישור" : "התחל ללא אישור נהג"}
          color="secondary"
          size="small"
          width={isMobile ? undefined : 150}
          onClick={onForceStartWithoutApproval}
        />
      ) : car.status === "occupied" ? (
        <Button
          label={isMobile ? "סיום" : "סיים נסיעת מבחן"}
          color="primary"
          size="small"
          width={isMobile ? undefined : 150}
          onClick={onEnd}
        />
      ) : null}
      <motion.div layout className={styles.dropdownMenu}>
        <div
          ref={triggerRef}
          onClick={handleToggleMenu}
          style={{ width: "25px", height: "25px", cursor: "pointer" }}
          role="button"
          tabIndex={0}
          aria-label="פעולות נוספות"
          aria-expanded={menuOpen}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleToggleMenu();
            }
          }}
        >
          <Menu3Dots />
        </div>
      </motion.div>
      {createPortal(
        <AnimatePresence>
          {menuOpen ? (
            <motion.div
              ref={portalMenuRef}
              className={styles.menuContent}
              style={{ ...menuPanelStyle, ...floatingStyle }}
              initial={{ opacity: 1, y: -6 }}
              animate={{ opacity: 1, y: -6 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0, ease: "easeOut" }}
            >
              {car.status === "pending" ? (
                <motion.div
                  layout
                  className={styles.menuItem}
                  onClick={() => {
                    closeMenu();
                    onCancelPending();
                  }}
                  style={{ ...menuItemStyle, userSelect: "none" }}
                >
                  בטל בקשת אישור
                </motion.div>
              ) : null}
              <motion.div
                layout
                className={styles.menuItem}
                onClick={() => {
                  closeMenu();
                  onFeedback();
                }}
                style={{ ...menuItemStyle, userSelect: "none" }}
              >
                משוב
              </motion.div>
              <motion.div
                layout
                className={styles.menuItem}
                onClick={() => {
                  closeMenu();
                  onHistory();
                }}
                style={{ ...menuItemStyle, userSelect: "none" }}
              >
                היסטוריה
              </motion.div>
              <motion.div
                layout
                className={styles.menuItem}
                onClick={() => {
                  closeMenu();
                  onExport();
                }}
                style={{ ...menuItemStyle, userSelect: "none" }}
              >
                ייצוא
              </motion.div>
              <motion.div
                layout
                className={styles.menuItem}
                onClick={() => {
                  closeMenu();
                  onDelete();
                }}
                style={{ ...menuItemStyle, userSelect: "none" }}
              >
                מחק
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.body
      )}
    </motion.div>
  );
};

export default TestDriveCarRowActions;
